import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Badges";
import { ExamInsights, InsightFilters } from "@/components/course/ExamInsights";

const fmt = (v, s = "") => (v === null || v === undefined ? "—" : `${v}${s}`);

function Overview() {
  const [f, setF] = useState({});
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); api.get("/insights/exams", { params: f }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))); }, [f]);
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Training" title="Exam insights" description="How learners do on each course exam, lowest average score first. Open a course to see which questions people miss most." />
      <InsightFilters f={f} setF={setF} />
      <div className="overflow-x-auto rounded-2xl border border-lasso-line bg-white">
        <table className="w-full min-w-[720px] text-sm" data-testid="insights-overview-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
            <tr><th className="px-5 py-3">Course</th><th>Avg score</th><th>Pass rate</th><th>Attempts to pass</th><th>Attempts</th><th>Bank</th><th className="pr-5">Needs review</th></tr>
          </thead>
          <tbody>
            {rows?.map((r) => (
              <tr key={r.id} className="border-t border-lasso-line hover:bg-lasso-tint" data-testid={`insights-row-${r.id}`}>
                <td className="px-5 py-3"><Link to={`/insights/exams/${r.id}`} className="font-semibold hover:underline" data-testid={`insights-open-${r.id}`}>{r.title}</Link>
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-400">{r.company_wide ? "Company-wide" : "Region-specific"}{!r.can_edit ? " · read-only" : ""}</span></td>
                <td className="font-semibold">{fmt(r.avg_score, "%")}</td>
                <td>{fmt(r.pass_rate, "%")}</td>
                <td>{fmt(r.avg_attempts_to_pass)}</td>
                <td>{r.attempts}</td>
                <td className="text-xs text-zinc-500">{r.per_attempt} of {r.bank_size}</td>
                <td className="pr-5">{r.needs_review > 0 ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600"><AlertTriangle className="h-3.5 w-3.5" />{r.needs_review}</span> : <span className="text-xs text-zinc-400">0</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows && <Loader2 className="m-6 h-5 w-5 animate-spin text-zinc-400" />}
      </div>
    </div>
  );
}

function Detail({ id }) {
  const [title, setTitle] = useState("");
  useEffect(() => { api.get(`/insights/exams/${id}`).then((r) => setTitle(r.data.course.title)).catch(() => {}); }, [id]);
  return (
    <div className="fade-up">
      <Link to="/insights/exams" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 hover:text-lasso-ink" data-testid="insights-back"><ArrowLeft className="h-4 w-4" /> All exams</Link>
      <PageHeader eyebrow="Exam insights" title={title || "Exam insights"} />
      <ExamInsights courseId={id} />
    </div>
  );
}

export default function ExamInsightsPage() {
  const { id } = useParams();
  return id ? <Detail id={id} /> : <Overview />;
}
